import React, { useState, useEffect } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { PENDING, IDLE } from "const"
import {
    MtDriveFile,
    MtDriveFileTag,
} from "server/models"
import { useInput, StateType, replaceHistoryState, qidIsOpened } from "client/utils"
import {
    StoreType,
    FileValidation,
    SaveDriveFile,
    SaveDriveFileTags,

    setDriveFileEdit,
    setDriveFileReloadList,
    setDriveFileReloadEdit,

    getFileLogo,
    parseName,
    createDriveUrl,
    createFileName,
    createRecycleFileName,
    convertFileSize,
    closeModal,
} from "client/store"
import {
    PageForm,
    Logo,
    FormGroup,
    Label,
    Input,
    Feedback,
    RadioButton,
    UserDropdown,
    DivInput,
    DateFormatter,
    UserIcon,
    Button,
    SpinnerIcon,
} from "client/components"

interface EditFileProps {
    file: MtDriveFile
    editAccess: boolean
}

let isMounted: boolean

const Form = ({
    file,
    editAccess,
}: EditFileProps) => {
    const drivePath = useSelector((state: StoreType) => state.drive.drivePath)
    const baseRoute = useSelector((state: StoreType) => state.drive.baseRoute)

    const outsideMode = useSelector((state: StoreType) => state.drive.outsideMode)
    const onSelectFile = useSelector((state: StoreType) => state.drive.onSelectFile)
    const onSelectFiles = useSelector((state: StoreType) => state.drive.onSelectFiles)
    const onSelectVersion = useSelector((state: StoreType) => state.drive.onSelectVersion)

    const accountUserList = useSelector((state: StoreType) => state.accountUser.accountUserList)
    const userList = useSelector((state: StoreType) => state.user.userList)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [name, setName] = useInput(createFileName(file))
    const [owner, setOwner] = useState(file.owner)
    const [datapointReference, setDatapointReference] = useState(file.datapointReference)
    const [selectedTags, setSelectedTags] = useState<number[]>([])

    const [validation, setValidation] = useState<FileValidation>(null)

    const [saveState, setSaveState] = useState<StateType>(IDLE)
    const [applyState, setApplyState] = useState<StateType>(IDLE)

    useEffect(() => {
        isMounted = true
        return () => {
            isMounted = false
        }
    }, [])

    useEffect(() => {
        if (!file.tags) return
        const tagIds: number[] = []
        file.tags.forEach((tag) => {
            tagIds.push(tag.tagId)
        })
        setSelectedTags(tagIds)
    }, [file])

    const onClose = () => {
        if (!isMounted) return
        dispatch(closeModal())
    }

    const onSave = (mode: number) => {
        if (mode === 1) setSaveState(PENDING)
        if (mode === 2) setApplyState(PENDING)

        const fileTags: MtDriveFileTag[] = []
        selectedTags.forEach((tagId) => {
            fileTags.push({
                fileId: file.id,
                tagId,
            })
        })

        const nameParsed = parseName(name)
        const newFileData: MtDriveFile = {
            ...file,
            name: nameParsed.name,
            copy: nameParsed.counter,
            owner,
            datapointReference,
            tags: fileTags,
        }

        Promise.all([
            SaveDriveFile(newFileData, lang),
            SaveDriveFileTags(file.id, fileTags),
        ]).then((res) => {
            if (isMounted) {
                setValidation(res[0].validation)
                setSaveState(IDLE)
                setApplyState(IDLE)
            }
            if (res[0].validation) return

            dispatch(setDriveFileEdit(newFileData))

            // update opened file url
            if (isMounted && !outsideMode && !onSelectFile && !onSelectFiles && !onSelectVersion) {
                const folderUrl = createDriveUrl(drivePath, baseRoute)
                const fileName = newFileData.folderId === null
                    ? createRecycleFileName(newFileData)
                    : createFileName(newFileData)
                const url = `${folderUrl}/${encodeURIComponent(fileName)}`
                replaceHistoryState(url)
            }

            dispatch(setDriveFileReloadList(false))
            setTimeout(() => {
                dispatch(setDriveFileReloadList(true))
            }, 0)

            if (mode === 1) onClose()
            if (mode === 2) {
                dispatch(setDriveFileReloadEdit(false))
                setTimeout(() => {
                    dispatch(setDriveFileReloadEdit(true))
                }, 0)
            }
        })
    }

    const fileLogo = getFileLogo(file, file.versions && file.versions[0])
    const logoStyle: React.CSSProperties = fileLogo.indexOf("data:image") >= 0
        ? {
            width: "unset",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "transparent",
        }
        : { background: "transparent" }

    // summ of all versions size
    let calculatedSize = 0
    if (file.versions && file.versions.length) {
        file.versions.forEach((version) => {
            calculatedSize += version.size
        })
    }
    const fileSize = file.versions && file.versions.length ? convertFileSize(calculatedSize) : ""

    const tagIds: number[] = []
    if (file.tags) {
        file.tags.forEach((tag) => {
            tagIds.push(tag.tagId)
        })
    }

    const accountUsers = (accountUserList && accountUserList[userData.accountId]) || []
    const qidPageOpened = qidIsOpened()
    return (
        <PageForm>
            <Row>
                <Col xs={12} md={4}>
                    <Logo logo={fileLogo} style={logoStyle} />
                </Col>
                <Col xs={12} md={8}>
                    <FormGroup>
                        <Label>{lang.name}</Label>
                        <Input type="text" value={name} onChange={setName} readOnly={!editAccess} />
                        <Feedback>{validation && validation.name}</Feedback>
                    </FormGroup>

                    <FormGroup>
                        <Label>{lang.owner}</Label>
                        {
                            editAccess ? (
                                <UserDropdown
                                    selectedId={owner}
                                    listIds={accountUsers}
                                    title={lang.changeOwner}
                                    showDelete={false}
                                    onChange={(userId) => setOwner(userId)}
                                />
                            ) : (
                                <DivInput><UserIcon user={userList[owner]} size="sm" /></DivInput>
                            )
                        }
                        <Feedback>{validation && validation.owner}</Feedback>
                    </FormGroup>

                    {
                        qidPageOpened && (
                            <FormGroup>
                                <Label>{lang.datapointReference}</Label>
                                {
                                    editAccess ? (
                                        <FormGroup inline>
                                            <RadioButton
                                                checked={datapointReference}
                                                onChange={() => setDatapointReference(true)}
                                                title={lang.yes}
                                            />
                                            <RadioButton
                                                checked={!datapointReference}
                                                onChange={() => setDatapointReference(false)}
                                                title={lang.no}
                                            />
                                        </FormGroup>
                                    ) : (
                                        <Input type="text" value={file.datapointReference ? lang.yes : lang.no} readOnly />
                                    )
                                }
                            </FormGroup>
                        )
                    }
                    {
                        // TODO: temporarily disabled
                        /*
                        qidPageOpened && (
                            <FormGroup>
                                <Label>{lang.tags}</Label>
                                <TagEditor
                                    selectedIds={selectedTags}
                                    onChange={(tagIds) => setSelectedTags(tagIds)}
                                    readOnly={!editAccess}
                                />
                            </FormGroup>
                        )
                        */
                    }

                    <FormGroup>
                        <Label>{lang.size}</Label>
                        <Input type="text" value={fileSize} readOnly />
                    </FormGroup>
                    <FormGroup>
                        <Label>{lang.modified}</Label>
                        <DivInput widePadding>
                            <DateFormatter date={file.updatedAt} defaultStyles />
                        </DivInput>
                    </FormGroup>

                    {
                        editAccess && (
                            <Row>
                                <Col xs={12}>
                                    <Button variant="success" row onClick={() => onSave(1)}>
                                        {saveState === PENDING ? <SpinnerIcon /> : lang.save}
                                    </Button>
                                    <Button variant="success" row onClick={() => onSave(2)}>
                                        {applyState === PENDING ? <SpinnerIcon /> : lang.apply}
                                    </Button>
                                    <Button row onClick={onClose}>{lang.cancel}</Button>
                                </Col>
                            </Row>
                        )
                    }
                </Col>
            </Row>
        </PageForm>
    )
}
export default Form
